<template>
  <div class="data-table-container">
    <!-- 表格头部 -->
    <div class="table-header">
      <div class="header-left">
        <h4>当月用户行为数据表</h4>
        <div class="table-stats">
          <span class="stat-item">
            <el-icon><User /></el-icon>
            总用户: <strong>{{ tableData.length }}</strong>
          </span>
          <span class="stat-item active">
            <el-icon><CircleCheck /></el-icon>
            活跃: <strong>{{ activeUserCount }}</strong>
          </span>
          <span class="stat-item inactive">
            <el-icon><Warning /></el-icon>
            非活跃: <strong>{{ inactiveUserCount }}</strong>
          </span>
          <span class="stat-item online">
            <el-icon><CircleCheck /></el-icon>
            在线: <strong>{{ onlineUserCount }}</strong>
          </span>
          <span class="stat-item offline">
            <el-icon><Warning /></el-icon>
            离线: <strong>{{ offlineUserCount }}</strong>
          </span>
        </div>
      </div>
      <div class="header-right">
        <div class="filter-controls">
          <el-select v-model="statusFilter" placeholder="筛选状态" size="small" style="width: 120px; margin-right: 8px;">
            <el-option label="全部用户" value="all" />
            <el-option label="活跃用户" value="active" />
            <el-option label="非活跃用户" value="inactive" />
            <el-option label="在线用户" value="online" />
            <el-option label="离线用户" value="offline" />
          </el-select>
          <el-input
            v-model="searchKeyword"
            placeholder="搜索用户名"
            size="small"
            style="width: 150px; margin-right: 8px;"
            clearable
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
        </div>
        <el-button type="primary" @click="analyzeProblems" :loading="analyzing" size="small">
          <el-icon><DataAnalysis /></el-icon>
          问题分析
        </el-button>
      </div>
    </div>

    <!-- 用户数据表格 - PC端 -->
    <div class="table-wrapper desktop-table">
      <el-table
          :data="filteredTableData"
          style="width: 100%"
          :height="320"
          :border="true"
          :stripe="true"
          :row-class-name="getRowClassName"
          :default-sort="{prop: 'orderCount', order: 'descending'}"
      >
        <el-table-column prop="userId" label="用户ID" width="100" fixed sortable align="center"/>
        <el-table-column prop="username" label="用户名" width="200" align="center" sortable>
          <template #default="{ row }">
            <div class="username-cell">
              <span>{{ row.username }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="loginCount" label="登录次数" width="120" align="center" sortable>
          <template #default="{ row }">
            <el-tag :type="getLoginCountTagType(row.loginCount)" size="small">
              <el-icon><User /></el-icon>
              {{ row.loginCount }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="orderCount" label="下单次数" width="120" align="center" sortable :sort-orders="['descending', 'ascending', null]">
          <template #default="{ row }">
            <el-tag :type="getOrderCountTagType(row.orderCount)" size="small">
              <el-icon><ShoppingCart /></el-icon>
              {{ row.orderCount }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="lastLogin" label="最后登录" width="180" align="center" sortable />
        <el-table-column prop="status" label="状态" width="100" align="center" sortable>
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)" size="small" effect="dark">
              <el-icon>
                <CircleCheck v-if="row.status === '活跃'" />
                <Warning v-else />
              </el-icon>
              {{ row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="isOnline" label="在线状态" width="150" align="center" sortable>
          <template #default="{ row }">
            <el-tag :type="getOnlineStatusType(row.isOnline)" size="small" effect="dark">
              <el-icon>
                <CircleCheck v-if="row.isOnline" />
                <Warning v-else />
              </el-icon>
              {{ row.isOnline ? '在线' : '离线' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="problems" label="问题分析" min-width="200">
          <template #default="{ row }">
            <div v-if="row.problems" class="problems-cell">
              <el-icon class="problem-icon"><InfoFilled /></el-icon>
              <span>{{ row.problems }}</span>
            </div>
            <span v-else class="no-problems">
              <el-icon><CircleCheck /></el-icon>
              表现良好
            </span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 用户数据卡片 - 移动端 -->
    <div class="mobile-cards">
      <div 
        v-for="user in paginatedMobileData" 
        :key="user.userId" 
        class="user-card"
        :class="{ 'active-card': user.status === '活跃', 'inactive-card': user.status === '非活跃' }"
      >
        <div class="card-header">
          <div class="user-info">
            <span class="user-id">ID: {{ user.userId }}</span>
            <span class="username">{{ user.username }}</span>
          </div>
          <div class="status-badges">
            <el-tag :type="getStatusType(user.status)" size="small" effect="dark">
              {{ user.status }}
            </el-tag>
            <el-tag :type="getOnlineStatusType(user.isOnline)" size="small">
              {{ user.isOnline ? '在线' : '离线' }}
            </el-tag>
          </div>
        </div>
        
        <div class="card-body">
          <div class="data-row">
            <div class="data-item">
              <span class="label">
                <el-icon><User /></el-icon>
                登录次数
              </span>
              <el-tag :type="getLoginCountTagType(user.loginCount)" size="small">
                {{ user.loginCount }}
              </el-tag>
            </div>
            <div class="data-item">
              <span class="label">
                <el-icon><ShoppingCart /></el-icon>
                下单次数
              </span>
              <el-tag :type="getOrderCountTagType(user.orderCount)" size="small">
                {{ user.orderCount }}
              </el-tag>
            </div>
          </div>
          
          <div class="data-row">
            <div class="data-item full-width">
              <span class="label">最后登录</span>
              <span class="value">{{ user.lastLogin }}</span>
            </div>
          </div>
          
          <div v-if="user.problems" class="card-footer">
            <div class="problems-info">
              <el-icon class="problem-icon"><InfoFilled /></el-icon>
              <span>{{ user.problems }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 分页 -->
      <div v-if="filteredTableData.length > 0" class="mobile-pagination">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[5, 10, 20, 50]"
          :total="filteredTableData.length"
          layout="total, sizes, prev, pager, next"
          size
          background
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <!-- 问题分析结果 -->
    <div v-if="showProblems" class="problem-analysis" ref="problemAnalysisRef">
      <h4>问题分析结果</h4>

      <!-- 问题统计概览 -->
      <div class="problem-summary">
        <div class="summary-card">
          <div class="summary-content">
            <div class="summary-number">{{ analysisResult.problemCount }}</div>
            <div class="summary-label">非活跃用户总数</div>
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-content">
            <div class="summary-number">{{ analysisResult.problemRatio }}%</div>
            <div class="summary-label">非活跃用户占比</div>
          </div>
        </div>
      </div>

      <!-- 非活跃用户详细列表 -->
      <div class="problem-users-section">
        <h5>非活跃用户详细列表</h5>
        
        <!-- PC端表格 -->
        <div class="problem-table-wrapper desktop-problem-table">
          <el-table
              :data="analysisResult.problemUsers"
              style="width: 100%"
              size="small"
              :height="200"
              :border="true"
          >
            <el-table-column prop="userId" label="用户ID" width="80" fixed />
            <el-table-column prop="username" label="用户名" width="200" align="center" />
            <el-table-column prop="loginCount" label="登录次数" width="100" align="center">
              <template #default="{ row }">
                <el-tag :type="getLoginCountType(row.loginCount)" size="small">
                  {{ row.loginCount }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="orderCount" label="下单次数" width="100" align="center">
              <template #default="{ row }">
                <el-tag :type="getOrderCountType(row.orderCount)" size="small">
                  {{ row.orderCount }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="lastLogin" label="上次登录时间" width="180" align="center" />
            <el-table-column prop="problemType" label="问题类型" width="120">
              <template #default="{ row }">
                <el-tag :type="getProblemTypeColor(row.problemType)" size="small">
                  {{ row.problemType }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="problems" label="问题描述" min-width="200" />
          </el-table>
        </div>
        
        <!-- 移动端卡片列表 -->
        <div class="mobile-problem-cards">
          <div 
            v-for="user in analysisResult.problemUsers" 
            :key="user.userId" 
            class="problem-user-card"
          >
            <div class="problem-card-header">
              <div class="user-basic-info">
                <span class="user-id">ID: {{ user.userId }}</span>
                <span class="username">{{ user.username }}</span>
              </div>
              <el-tag :type="getProblemTypeColor(user.problemType)" size="small">
                {{ user.problemType }}
              </el-tag>
            </div>
            
            <div class="problem-card-body">
              <div class="problem-data-row">
                <div class="problem-data-item">
                  <span class="label">登录次数</span>
                  <el-tag :type="getLoginCountType(user.loginCount)" size="small">
                    {{ user.loginCount }}
                  </el-tag>
                </div>
                <div class="problem-data-item">
                  <span class="label">下单次数</span>
                  <el-tag :type="getOrderCountType(user.orderCount)" size="small">
                    {{ user.orderCount }}
                  </el-tag>
                </div>
              </div>
              
              <div class="problem-data-row">
                <div class="problem-data-item full-width">
                  <span class="label">上次登录</span>
                  <span class="value">{{ user.lastLogin }}</span>
                </div>
              </div>
              
              <div v-if="user.problems" class="problem-description">
                <span class="label">问题描述：</span>
                <span class="desc-text">{{ user.problems }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 问题分析建议 -->
      <div class="problem-suggestions">
        <h5>分析建议</h5>
        <div class="suggestions-list">
          <el-alert
              v-for="(suggestion, index) in analysisResult.suggestions"
              :key="`suggestion-${index}`"
              :title="suggestion.title"
              :description="suggestion.description"
              :type="suggestion.level"
              show-icon
              :closable="false"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, nextTick } from 'vue'
import { 
  User, 
  CircleCheck, 
  Warning, 
  Search, 
  DataAnalysis, 
  ShoppingCart, 
  InfoFilled 
} from '@element-plus/icons-vue'

// Props
const props = defineProps({
  tableData: {
    type: Array,
    default: () => []
  },
  loginThreshold: {
    type: Number,
    default: 5
  },
  orderThreshold: {
    type: Number,
    default: 0
  }
})

// Emits
const emit = defineEmits(['problem-analyzed'])

const showProblems = ref(false)
const analyzing = ref(false)
const statusFilter = ref('all')
const searchKeyword = ref('')

// 分页相关
const currentPage = ref(1)
const pageSize = ref(10)

// 问题分析区域的引用
const problemAnalysisRef = ref(null)

// 使用reactive存储分析结果，避免频繁的响应式更新
const analysisResult = reactive({
  problemUsers: [],
  suggestions: [],
  problemCount: 0,
  problemRatio: '0.0'
})

// 计算属性：活跃用户数量
const activeUserCount = computed(() => {
  return props.tableData.filter(user => user.status === '活跃').length
})

// 计算属性：非活跃用户数量
const inactiveUserCount = computed(() => {
  return props.tableData.filter(user => user.status === '非活跃').length
})

// 计算属性：在线用户数量
const onlineUserCount = computed(() => {
  return props.tableData.filter(user => user.isOnline === true).length
})

// 计算属性：离线用户数量
const offlineUserCount = computed(() => {
  return props.tableData.filter(user => user.isOnline === false).length
})

// 计算属性：过滤后的表格数据
const filteredTableData = computed(() => {
  let filtered = [...props.tableData] // 创建副本避免修改原数组

  // 状态筛选
  if (statusFilter.value === 'active') {
    filtered = filtered.filter(user => user.status === '活跃')
  } else if (statusFilter.value === 'inactive') {
    filtered = filtered.filter(user => user.status === '非活跃')
  } else if (statusFilter.value === 'online') {
    filtered = filtered.filter(user => user.isOnline === true)
  } else if (statusFilter.value === 'offline') {
    filtered = filtered.filter(user => user.isOnline === false)
  }

  // 用户名搜索
  if (searchKeyword.value) {
    filtered = filtered.filter(user => 
      user.username.toLowerCase().includes(searchKeyword.value.toLowerCase())
    )
  }

  return filtered
})

// 计算属性：移动端分页数据
const paginatedMobileData = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  const end = start + pageSize.value
  return filteredTableData.value.slice(start, end)
})

// 行样式类名
const getRowClassName = ({ row }) => {
  if (row.status === '活跃') {
    return 'active-user-row'
  } else if (row.status === '非活跃') {
    return 'inactive-user-row'
  }
  return ''
}


// 状态类型映射函数
const getStatusType = (status) => {
  return status === '活跃' ? 'success' : 'danger'
}

// 登录次数标签类型
const getLoginCountTagType = (count) => {
  if (count >= props.loginThreshold) {
    return 'success'
  } else if (count > 0) {
    return 'warning'
  } else {
    return 'danger'
  }
}

// 下单次数标签类型
const getOrderCountTagType = (count) => {
  if (count >= props.orderThreshold) {
    return 'success'
  } else if (count > 0) {
    return 'warning'
  } else {
    return 'danger'
  }
}

const getLoginCountType = (count) => {
  return count < props.loginThreshold ? 'danger' : 'success'
}

const getOrderCountType = (count) => {
  return count < props.orderThreshold ? 'danger' : 'success'
}

const getProblemTypeColor = (type) => {
  const colorMap = {
    '严重非活跃': 'danger',
    '登录不足': 'warning',
    '转化率低': 'info'
  }
  return colorMap[type] || 'info'
}

// 在线状态标签类型
const getOnlineStatusType = (isOnline) => {
  return isOnline ? 'success' : 'info'
}

// 分页事件处理
const handleSizeChange = (val) => {
  pageSize.value = val
  currentPage.value = 1 // 改变每页大小时重置到第一页
}

const handleCurrentChange = (val) => {
  currentPage.value = val
}

const analyzeProblems = async () => {
  analyzing.value = true

  try {
    // 使用 setTimeout 模拟异步处理，避免阻塞UI
    await new Promise(resolve => setTimeout(resolve, 300))

    // 根据筛选条件识别问题用户
    const problemUsers = props.tableData.filter(user => {
      return user.loginCount < props.loginThreshold || user.orderCount < props.orderThreshold
    }).map(user => {
      // 确定问题类型
      let problemType = ''
      if (user.loginCount < props.loginThreshold && user.orderCount < props.orderThreshold) {
        problemType = '严重非活跃'
      } else if (user.loginCount < props.loginThreshold) {
        problemType = '登录不足'
      } else if (user.orderCount < props.orderThreshold) {
        problemType = '转化率低'
      }

      return {
        ...user,
        problemType
      }
    })

    // 计算统计数据
    const totalUsers = props.tableData.length
    const problemCount = problemUsers.length
    const problemRatio = totalUsers > 0 ? (problemCount / totalUsers * 100).toFixed(1) : '0.0'

    const loginIssueUsers = problemUsers.filter(u => u.loginCount < props.loginThreshold).length
    const conversionIssueUsers = problemUsers.filter(u => u.orderCount < props.orderThreshold).length

    // 生成分析建议
    const suggestions = []

    if (loginIssueUsers > 0) {
      suggestions.push({
        title: `登录活跃度问题 (${loginIssueUsers}名用户)`,
        description: `有${loginIssueUsers}名用户登录次数<${props.loginThreshold}次，建议通过推送通知、积分奖励等方式提升用户登录频次`,
        level: 'warning'
      })
    }

    if (conversionIssueUsers > 0) {
      suggestions.push({
        title: `转化率问题 (${conversionIssueUsers}名用户)`,
        description: `有${conversionIssueUsers}名用户下单次数<${props.orderThreshold}次，建议优化产品推荐算法、提供新用户优惠券等措施`,
        level: 'error'
      })
    }

    if (parseFloat(problemRatio) >= 50) {
      suggestions.push({
        title: '整体用户活跃度偏低',
        description: `问题用户占比达到${problemRatio}%，建议从产品体验、用户运营等多个维度进行整体优化`,
        level: 'error'
      })
    }

    // 批量更新分析结果
    Object.assign(analysisResult, {
      problemUsers,
      suggestions,
      problemCount,
      problemRatio
    })

    showProblems.value = true

    // 等待DOM更新后滚动到问题分析区域
    await nextTick()
    if (problemAnalysisRef.value) {
      problemAnalysisRef.value.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }

    // 向父组件发送分析结果
    emit('problem-analyzed', {
      problemUsers,
      suggestions,
      statistics: {
        total: totalUsers,
        problemCount,
        problemRatio
      }
    })

  } catch (error) {
    console.error('分析过程中出现错误:', error)
  } finally {
    analyzing.value = false
  }
}

// 暴露方法给父组件
defineExpose({
  analyzeProblems,
  showProblems
})
</script>

<style scoped>
.data-table-container {
  width: 100%;
  background-color: #fff;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  padding: 0 4px;
  flex-wrap: wrap;
  gap: 16px;
}

.header-left h4 {
  margin: 0 0 8px 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.table-stats {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #606266;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: #f5f7fa;
}

.stat-item.active {
  background-color: #f0f9ff;
  color: #67c23a;
}

.stat-item.inactive {
  background-color: #fef0f0;
  color: #f56c6c;
}

.stat-item.online {
  background-color: #f0f9ff;
  color: #67c23a;
}

.stat-item.offline {
  background-color: #f4f4f5;
  color: #909399;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 表格容器固定高度，避免布局抖动 */
.table-wrapper {
  margin-bottom: 16px;
}

/* 默认显示表格，隐藏卡片 */
.desktop-table {
  display: block;
}

.mobile-cards {
  display: none;
}

/* 用户名单元格样式 */
.username-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}


/* 问题分析单元格样式 */
.problems-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #e6a23c;
}

.problem-icon {
  color: #f56c6c;
}

.no-problems {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #67c23a;
  font-size: 13px;
}

/* 表格行样式 */
:deep(.active-user-row) {
  background-color: #f0f9ff !important;
}

:deep(.active-user-row:hover) {
  background-color: #e1f5fe !important;
}

:deep(.inactive-user-row) {
  background-color: #fef0f0 !important;
}

:deep(.inactive-user-row:hover) {
  background-color: #fce4e4 !important;
}

/* 表格排序图标颜色 */
:deep(.el-table th.is-sortable .el-table__column-filter-trigger) {
  color: #409eff;
}

/* 移动端卡片样式 */
.user-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.user-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.user-card.active-card {
  border-left: 4px solid #67c23a;
  background-color: #f0f9ff;
}

.user-card.inactive-card {
  border-left: 4px solid #f56c6c;
  background-color: #fef0f0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.user-id {
  font-size: 12px;
  color: #909399;
  font-weight: 500;
}

.username {
  font-size: 16px;
  color: #303133;
  font-weight: 600;
}

.status-badges {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.data-row {
  display: flex;
  gap: 12px;
}

.data-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background-color: #f5f7fa;
  padding: 10px;
  border-radius: 6px;
}

.data-item.full-width {
  flex: 1 1 100%;
}

.data-item .label {
  font-size: 12px;
  color: #606266;
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
}

.data-item .value {
  font-size: 13px;
  color: #303133;
  font-weight: 500;
}

.card-footer {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
}

.problems-info {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #e6a23c;
  font-size: 13px;
}

.problems-info .problem-icon {
  color: #f56c6c;
}

.mobile-pagination {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}

/* 响应式设计 */
@media screen and (max-width: 768px) {
  .table-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .header-right {
    justify-content: flex-start;
  }
  
  .filter-controls {
    flex-wrap: wrap;
    width: 100%;
  }
  
  .filter-controls .el-select,
  .filter-controls .el-input {
    width: 100% !important;
    margin-right: 0 !important;
    margin-bottom: 8px;
  }
  
  .header-right .el-button {
    width: 100%;
  }
  
  .table-stats {
    justify-content: flex-start;
  }
  
  .stat-item {
    font-size: 12px;
    padding: 3px 6px;
  }
  
  /* 隐藏表格，显示卡片 */
  .desktop-table {
    display: none !important;
  }
  
  .mobile-cards {
    display: block !important;
  }
  
  /* 移动端分页样式优化 */
  .mobile-pagination :deep(.el-pagination) {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .mobile-pagination :deep(.el-pagination__sizes) {
    margin: 0 4px 8px 0;
  }
  
  .mobile-pagination :deep(.el-pagination__jump) {
    margin-left: 0;
  }
  
  /* 问题分析部分移动端适配 */
  .problem-summary {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .summary-card {
    padding: 16px;
  }
  
  .summary-number {
    font-size: 20px;
  }
  
  .summary-label {
    font-size: 12px;
  }
  
  /* 隐藏问题分析表格，显示卡片 */
  .desktop-problem-table {
    display: none !important;
  }
  
  .mobile-problem-cards {
    display: block !important;
  }
  
  .problem-user-card {
    padding: 12px;
  }
  
  .problem-card-header {
    margin-bottom: 10px;
    padding-bottom: 8px;
  }
  
  .problem-suggestions h5 {
    font-size: 14px;
  }
  
  .suggestions-list :deep(.el-alert__title) {
    font-size: 13px;
  }
  
  .suggestions-list :deep(.el-alert__description) {
    font-size: 12px;
  }
}

.problem-analysis {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 2px solid #f0f2f5;
}

.problem-analysis h4 {
  margin: 0 0 20px 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

/* 问题统计概览 - 使用固定布局避免重新计算 */
.problem-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

.summary-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.summary-content {
  /* 固定内容区域高度 */
  height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.summary-number {
  font-size: 24px;
  font-weight: bold;
  color: #e74c3c;
  margin-bottom: 4px;
  line-height: 1;
}

.summary-label {
  font-size: 13px;
  color: #666;
  line-height: 1;
}

/* 问题用户列表 */
.problem-users-section {
  margin-bottom: 24px;
}

.problem-users-section h5 {
  margin: 0 0 16px 0;
  color: #303133;
  font-size: 15px;
  font-weight: 600;
}

/* 固定问题表格高度 */
.problem-table-wrapper {
  /* 固定容器，避免高度变化 */
  min-height: 200px;
}

/* 默认显示PC端表格，隐藏移动端卡片 */
.desktop-problem-table {
  display: block;
}

.mobile-problem-cards {
  display: none;
}

/* 问题用户卡片样式 */
.problem-user-card {
  background: #fff;
  border: 1px solid #f56c6c;
  border-left: 4px solid #f56c6c;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  box-shadow: 0 2px 4px rgba(245, 108, 108, 0.1);
}

.problem-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f5f7fa;
}

.user-basic-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.problem-card-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.problem-data-row {
  display: flex;
  gap: 10px;
}

.problem-data-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background-color: #fef0f0;
  padding: 8px;
  border-radius: 6px;
}

.problem-data-item.full-width {
  flex: 1 1 100%;
}

.problem-data-item .label {
  font-size: 12px;
  color: #606266;
  font-weight: 500;
}

.problem-data-item .value {
  font-size: 13px;
  color: #303133;
}

.problem-description {
  background-color: #fff7e6;
  padding: 10px;
  border-radius: 6px;
  border-left: 3px solid #e6a23c;
}

.problem-description .label {
  font-size: 12px;
  color: #606266;
  font-weight: 600;
  display: block;
  margin-bottom: 4px;
}

.problem-description .desc-text {
  font-size: 13px;
  color: #303133;
  line-height: 1.5;
}

/* 问题分析建议 */
.problem-suggestions h5 {
  margin: 0 0 16px 0;
  color: #303133;
  font-size: 15px;
  font-weight: 600;
}

.suggestions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* 确保Alert组件不会导致布局重计算 */
.suggestions-list :deep(.el-alert) {
  margin-bottom: 0 !important;
}

/* 覆盖Element Plus表格样式，避免动态高度计算 */
:deep(.el-table) {
  /* 固定表格布局 */
  table-layout: fixed !important;
}

:deep(.el-table__body-wrapper) {
  /* 避免滚动条导致的重新计算 */
  overflow-y: auto !important;
}
</style>